import { useState } from 'react';
import { XIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type LocationOption = { id: string; riskId?: number | string; name?: string; [field: string]: unknown };

/** The child locations of a group: one field holding a chip per chosen location, opening a list of the rest. */
export default function MultiSelect({
  selectedValues = [],
  setSelectedValues,
  options,
}: {
  selectedValues?: LocationOption[];
  setSelectedValues: (values: LocationOption[]) => void;
  options: LocationOption[];
}) {
  const [open, setOpen] = useState(false);
  const available = options.filter((opt) => !selectedValues.some((sel) => sel.id === opt.id));

  return (
    <div className="flex h-full w-full p-2">
      <Popover open={open} onOpenChange={setOpen}>
        {/* The field is a div, not a button, because each chip inside it holds its own remove button. */}
        <PopoverTrigger
          nativeButton={false}
          render={
            <div className="mt-2.5 flex min-h-9 w-full cursor-pointer flex-row flex-wrap items-center rounded-sm border border-picker-border bg-white p-1.5" />
          }
        >
          {selectedValues.length > 0 ? (
            selectedValues.map((val) => (
              <Badge key={val.id} variant="secondary" className="mr-1.5 mb-1 h-auto gap-1 rounded-xl bg-primary/20 px-2 py-1 text-xs text-primary">
                {`${val.riskId}. ${val.name}`}
                <button
                  type="button"
                  aria-label={`Remove ${val.riskId}. ${val.name}`}
                  className="rounded-full p-0.5 font-bold hover:bg-primary/20"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedValues(selectedValues.filter((item) => item.id !== val.id));
                  }}
                >
                  <XIcon className="size-3" />
                </button>
              </Badge>
            ))
          ) : (
            <span className="text-sm">Select locations</span>
          )}
        </PopoverTrigger>
        <PopoverContent align="start" className="max-h-[150px] w-(--anchor-width) gap-0 overflow-y-auto p-1">
          {options.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground">No locations available</p>
          ) : (
            available.map((item) => (
              <button
                key={String(item.id)}
                type="button"
                className="w-full rounded-sm p-1 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => setSelectedValues([...selectedValues, item])}
              >
                {` ${item.riskId}. ${item.name}`}
              </button>
            ))
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
